import React from 'react'
import moment from 'moment'
import { ModalTitle, ModalInfo } from 'src/styles/pages/Scheduling'
import UserIconRounded from 'static/icons/pet-anjo-user-circle.svg'
import LockAlt from 'static/icons/lock-alt.svg'
import PhoneAltIcon from 'static/icons/phone-alt.svg'
import { assetPrefix } from 'config/env_config'
// Components
import ProfessionalProfile from 'components/professionalProfile'
import LoginForm from 'components/loginForm/v2'
import SignupForm from 'components/signupForm'
import Terms from 'components/terms'
import PaymentPackageTerms from 'components/paymentPackageTerms'
import PackageAdhesionTerms from 'src/components/packageAdhesionTerms'
import ConfirmAccountForm from 'components/confirmAccountForm'
import ForgotPasswordForm from 'components/forgotPasswordForm'
import ResetPasswordForm from 'components/resetPasswordForm'
import IncompletePetDataForm from '../components/incompletePetDataForm'
import SchedulingTerms from 'src/components/schedulingTerms'

const siteRecaptchaKey = process.env.RECAPTCHA_SITE_KEY_LOGIN

const SCHEDULE_ERROR_GENERIC = (onConfirm, phone) => ({
  type: 'warning',
  title: 'Desculpe, no momento este agendamento não pode ser realizado.',
  body: `Você pode marcar um horário diretamente na loja ou pelo telefone ${phone}.`,
  cancelLabel: 'Fechar',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`,
  onConfirm: onConfirm
})

const PARTNER_NOT_FOUND = (storeName) => ({
  type: 'warning',
  title: 'Desculpe, não encontramos um resultado para essa busca',
  cancelLabel: 'Fechar',
  body: (
    <>
      Sugerimos que tente encontrar pelo CEP, ativar sua localização ou consultar as unidades disponíveis em{' '}
      <a href='https://www.cobasi.com.br/institucional/nossas-lojas'>Nossas lojas</a>.
    </>
  ),
  fullButton: true,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`
})

const STEP_UPDATE = (onConfirm, onCancel) => ({
  type: 'warning',
  title: 'Atenção: Alteração de etapa necessária',
  body: (
    <>
      Uma das etapas deste agendamento<br />
      precisa ser escolhida novamente. Altere a<br />
      etapa ou crie um novo agendamento.<br />
    </>
  ),
  outlineCancelButton: true,
  fullButton: true,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`,
  cancelLabel: 'Novo agendamento',
  confirmLabel: 'Alterar etapa',
  onConfirm: onConfirm,
  onCancel: onCancel
})

const REPEAT_SCHEDULE_DATE_SELECTION = (onConfirm, onCancel, schedulingInfo) => ({
  type: 'warning',
  title: (
    <>
      Repetir Agendamento: <br />
      Escolha a nova data!
    </>
  ),
  body: (
    <>
      Confira os detalhes deste  <br />agendamento: <br />
      <b>Serviço:</b> {schedulingInfo?.service}<br />
      <b>Loja:</b> {schedulingInfo?.partner}<br />
      <b>Profissional:</b> {schedulingInfo?.professional}<br /><br />
      Clique em ”Continuar” para escolher a<br /> nova data e o horário.
    </>
  ),
  outlineCancelButton: true,
  fullButton: true,
  icon: `${assetPrefix}/static/icons/Repeat.svg`,
  cancelLabel: 'Cancelar',
  confirmLabel: 'Continuar',
  onConfirm: onConfirm,
  onCancel: onCancel,
  titleSize: '16px',
  iconSize: '48px'
})

const EDIT_PET_WARNING = (onConfirm) => ({
  type: 'warning',
  title: 'Tem certeza que deseja editar as informações do pet agora?',
  body: 'Ao redirecionar para edição dos dados do pet, todas as informações deste agendamento serão perdidas.',
  confirmLabel: 'Continuar',
  cancelLabel: 'Voltar para o agendamento',
  fullButton: true,
  outlineCancelButton: true,
  icon: `${assetPrefix}/static/icons/triangle-exclamation.svg`,
  onConfirm: onConfirm
})

const NEW_PET_WARNING = (onConfirm) => ({
  type: 'warning',
  title: 'Tem certeza que deseja cadastrar um novo pet agora?',
  body: 'Ao redirecionar para cadastrar o novo pet, todas as informações deste agendamento serão perdidas.',
  confirmLabel: 'Continuar',
  cancelLabel: 'Voltar para o agendamento',
  outlineCancelButton: true,
  fullButton: true,
  icon: `${assetPrefix}/static/icons/triangle-exclamation.svg`,
  onConfirm: onConfirm
})

const LOCATION_NOT_ALLOWED = () => ({
  type: 'warning',
  title: 'Localização indisponível',
  cancelLabel: 'Fechar',
  body: (
    <>
      Estamos sem acesso à sua localização. Por favor, insira o seu endereço manualmente ou autorize nas permissões do seu aparelho.
    </>
  ),
  fullButton: true,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`
})

const DUPLICATED_SCHEDULING_WARNING = (onConfirm, errorMessage) => ({
  visible: true,
  type: 'warning',
  title: 'Atenção: Agendamento duplicado',
  confirmLabel: 'Escolher nova data',
  onConfirm: onConfirm,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`,
  fullButton: true,
  showClose: false,
  body: (
    <>
      {errorMessage.split('\n')?.map((str, index) => (
        <div key={index}>
          <ModalInfo noMarginBottom>{str}</ModalInfo>
          <br />
        </div>
      ))}
      <ModalInfo>Ex: Se você tem um agendamento para o dia 19, não conseguirá realizar um novo nos dias 18 e 20.</ModalInfo>
    </>
  )
})

const PROFESSIONAL_PROFILE = (professional, servicesList, partner) => ({
  professional: true,
  type: 'professionalInfo',
  title: '',
  body: (
    <ProfessionalProfile
      professional={professional}
      servicesList={servicesList}
      partner={partner}
    />
  )
})

const getReschedulingModalBody = (hasSchedulingChanged, scheduleBody, schedulingData, havePayment, professionalName) => {
  if (hasSchedulingChanged) {
    return (
      <>
        <ModalInfo>Confirme os novos dados do reagendamento</ModalInfo>
        <ModalInfo>
          <strong>Profissional:</strong> {professionalName}
        </ModalInfo>
        <ModalInfo>
          <strong>Data:</strong>{' '}
          {moment(scheduleBody.selectDay).format('DD/MM/YYYY')}
        </ModalInfo>
        <ModalInfo>
          <strong>Horário:</strong> {scheduleBody.selectTime}
        </ModalInfo>
        <br />
        <ModalInfo>
          Importante: O reagendamento é gratuito, verifique com a recepção se as mudanças feitas podem ter alterado o valor.
        </ModalInfo>
      </>
    )
  } else {
    return (
      <>
        <ModalInfo>Confirme os novos dados do reagendamento</ModalInfo>
        <ModalInfo>
          <strong>Data:</strong>{' '}
          {moment(scheduleBody.selectDay).format('DD/MM/YYYY')}
        </ModalInfo>
        <ModalInfo>
          <strong>Horário:</strong> {scheduleBody.selectTime}
        </ModalInfo>
        <ModalInfo>
          O seu reagendamento não vai gerar nenhum valor adicional.
        </ModalInfo>
        <ModalInfo>
          Tem certeza que deseja <strong>reagendar o serviço?</strong>
        </ModalInfo>
      </>
    )
  }
}

const RESCHEDULING = (onConfirm, onCancel, hasSchedulingChanged, scheduleBody, schedulingData, havePayment, professionalName) => ({
  type: 'action',
  title: <ModalTitle>Você confirma o reagendamento do serviço?</ModalTitle>,
  cancelLabel: 'Agora não',
  outlineCancelButton: true,
  confirmLabel: 'Confirmar reagendamento',
  onConfirm: onConfirm,
  onCancel: onCancel,
  iconSize: !hasSchedulingChanged ? 70 : undefined,
  icon: `${assetPrefix}/static/icons/calendar-check.svg`,
  body: getReschedulingModalBody(hasSchedulingChanged, scheduleBody, schedulingData, havePayment, professionalName)
})

const INCOMPLETE_PET_FORM = (pet, onConfirm) => ({
  title: 'Cadastro do Pet',
  icon: `${assetPrefix}/static/icons/warningIcon.svg`,
  showBackButton: false,
  overflow: 'unset',
  overflowIncompletePet: 'unset',
  body: (<><IncompletePetDataForm pet={pet} onSuccess={onConfirm} /></>)
})

const LOGGOUT_MODAL = (onCancel, onConfirm) => ({
  title: 'Confirmação',
  type: 'warning',
  icon: `${assetPrefix}/static/icons/loggout-icon.svg`,
  cancelLabel: 'Cancelar',
  confirmLabel: 'Sair',
  onCancel: onCancel,
  onConfirm: onConfirm,
  showBackButton: false,
  body: <>Deseja realmente sair?</>
})

const PET_SAVE_SUCCESS = () => ({
  title: 'Dados salvos com sucesso',
  type: 'warning',
  icon: `${assetPrefix}/static/icons/check-green-circle.svg`,
  showBackButton: false,
  cancelLabel: 'Fechar'
})

const RESCHEDULING_SUCCESS = (onCancel) => ({
  showButtons: false,
  title: <ModalTitle>Seu serviço foi alterado com sucesso.</ModalTitle>,
  icon: `${assetPrefix}/static/icons/check-green-circle.svg`,
  cancelLabel: 'Fechar',
  onCancel: onCancel,
  showClose: false
})

const LOGIN_MODAL = ({ onCancel, callback }) => ({
  title: 'Acesse sua conta',
  subtitle: 'Personalize sua experiência e facilite seu agendamento',
  showBackButton: true,
  icon: <UserIconRounded />,
  children: (
    <LoginForm
      successCallback={callback}
      dataTest='login'
      onCancel={onCancel}
      cancelLabel='Acessar depois'
      confirmLabel={siteRecaptchaKey ? 'Acessar conta' : undefined}
    />
  )
})
const FORGOT_PASSWORD_MODAL = ({ onCancel, callback }) => ({
  title: 'Esqueceu a senha?',
  subtitle: 'Insira seu email para enviarmos um código para redefinir sua senha',
  decentralizedSubTitle: true,
  showBackButton: true,
  children: (
    <ForgotPasswordForm
      successCallback={callback}
      dataTest='login'
      onCancel={onCancel}
      confirmLabel='Enviar código'
    />
  ),
  largeHeaderGap: true
})
const RESET_PASSWORD_MODAL = ({ onCancel, callback }) => ({
  title: 'Redefinir senha',
  subtitle: 'Escolha uma nova senha seguindo os critérios abaixo',
  showBackButton: true,
  icon: <LockAlt />,
  children: (
    <ResetPasswordForm
      successCallback={callback}
      dataTest='login'
      onCancel={onCancel}
      cancelLabel='Voltar'
      confirmLabel='Salvar'
    />
  )
})
const SCHEDULE_RESERVED_LOGIN = ({ onCancel, callback }) => ({
  title: 'Acesse sua conta',
  subtitle: 'Para concluir o agendamento acesse sua conta ou crie uma nova.',
  showBackButton: true,
  icon: <UserIconRounded />,
  onCancel: onCancel,
  children: (
    <LoginForm
      successCallback={callback}
      dataTest='login'
      confirmLabel={siteRecaptchaKey ? 'Fazer login' : undefined}
    />
  )
})

const CREATE_ACCOUNT = ({ onCancel, callback, formData }) => ({
  title: 'Criar conta',
  showBackButton: true,
  onCancel: onCancel,
  children: <SignupForm formData={formData} onAccountCreated={callback} />
})

const USE_TERMS = ({ visible, callback }) => ({
  visible,
  dataTest: 'use-terms',
  showClose: false,
  children: <Terms onTermsAction={callback} />
})

const PAYMENT_PACKAGE_TERMS = ({ visible, callback, onClose, hideActionButtons }) => ({
  visible,
  dataTest: 'payment-package-terms',
  onClose,
  modalWidth: '760px',
  modalTop: '50px',
  children: <PaymentPackageTerms onTermsAction={callback} onClose={() => onClose()} hideActionButtons={hideActionButtons} />
})

const PACKAGE_ADHESION_TERMS = ({ visible, callback, onClose, hideActionButtons = true }) => ({
  visible,
  dataTest: 'package-adhesion-terms',
  onClose,
  modalWidth: '760px',
  modalTop: '50px',
  children: (
    <PackageAdhesionTerms
      onTermsAction={callback}
      onClose={() => onClose?.()}
      hideActionButtons={hideActionButtons}
    />
  )
})

const CONFIRM_NEW_ACCOUNT = ({ onCancel, ...props }) => ({
  title: 'Código de acesso',
  subtitle: 'Informe o código de acesso enviado para o e-mail da sua conta de acesso e clique em acessar conta.',
  decentralizedSubTitle: true,
  showBackButton: true,
  onCancel: onCancel,
  children: <ConfirmAccountForm {...props} />,
  largeHeaderGap: true
})

const SUCCESS_GENERIC = ({ title, message, confirmLabel, onConfirm }) => ({
  visible: true,
  type: 'info',
  title: title ?? 'Sucesso',
  body: message ?? '',
  confirmLabel: confirmLabel,
  fullButton: true,
  outlineCancelButton: true,
  icon: `${assetPrefix}/static/icons/hyper-check.svg`,
  onConfirm: onConfirm,
  noPadding: true,
  showClose: false
})

const EDIT_MULTIPLE_SCHEDULING = ({ onCancel, onConfirm }) => ({
  visible: true,
  cancelLabel: 'Agora não',
  confirmLabel: 'Alterar agendamento',
  type: 'info',
  title: 'Atenção',
  subtitle: 'Ao editar este agendamento, você perderá as informações do agendamento que está em andamento.',
  fullButton: true,
  outlineCancelButton: true,
  icon: `${assetPrefix}/static/icons/warningIcon.svg`,
  noPadding: true,
  showClose: false,
  dataTest: 'modal-edit-multiple-scheduling',
  onConfirm: onConfirm,
  onCancel: onCancel
})

const PAYMENT_PROBLEM = ({ onCancel, onConfirm, onClose, isServicePackage }) => ({
  confirmLabel: isServicePackage ? 'Agora não' : 'Pagamento no local',
  cancelLabel: isServicePackage ? 'Alterar informações' : 'Revisar informações',
  type: 'danger',
  title: `Pagamento não realizado ${isServicePackage ? 'no momento' : ''}`,
  subtitle: `Isso pode ocorrer por instabilidade na operadora ou dados incorretos. Por favor, revise ${isServicePackage ? 'e altere essas informações' : 'essas informações ou siga para pagamento no local'}.`,
  fullButton: true,
  outlineConfirmButton: true,
  icon: `${assetPrefix}/static/icons/payment-error.svg`,
  noPadding: true,
  showClose: false,
  dataTest: 'payment-problem',
  onConfirm: isServicePackage ? onClose : onConfirm,
  onCancel: onCancel,
  onClose: onClose,
  iconSize: '100%'
})

const PAYMENT_SUCCESS = ({ onConfirm, onCancel, isServicePackage }) => ({
  confirmLabel: isServicePackage ? 'Agora não' : undefined,
  onConfirm: isServicePackage ? onConfirm : undefined,
  outlineConfirmButton: true,
  cancelLabel: isServicePackage ? 'Ir para agendamento' : 'Fechar',
  onCancel,
  type: 'success',
  title: 'Pagamento e agendamento realizados com sucesso',
  subtitle: isServicePackage
    ? 'Aproveite para já agendar o primeiro banho do seu pacote'
    : 'Caso queira um serviço adicional, combine diretamente com a recepção',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/payment-successful.svg`,
  noPadding: true,
  showClose: false,
  dataTest: 'payment-success',
  iconSize: '100%'
})

const PAYMENT_PROCESSING = ({ onConfirm, isServicePackage }) => ({
  confirmLabel: 'Fechar',
  type: 'success',
  title: isServicePackage ? 'Confirmação em andamento' : 'Estamos confirmando seu agendamento',
  subtitle: isServicePackage ? 'Pode levar alguns minutos para o sistema confirmar a compra do pacote. Por favor, aguarde um momento, ele aparecerá em ”Minha agenda”' : 'Pode levar alguns minutos para o sistema confirmar o seu atendimento. Por favor, aguarde um instante e confira na opção ”Meus agendamentos”',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/payment-warning.svg`,
  noPadding: true,
  showClose: false,
  dataTest: 'payment-success',
  onConfirm: onConfirm,
  iconSize: '100%'
})

const ACCOUNT_CREATE_SUCCESS = (onConfirm) => ({
  title: 'Conta criada com sucesso.',
  type: 'warning',
  icon: `${assetPrefix}/static/icons/check-green-circle.svg`,
  showBackButton: false,
  confirmLabel: 'Fechar',
  onConfirm: onConfirm
})

const PACKAGE_CANCEL = ({ onConfirm, onCancel, onClose }) => ({
  confirmLabel: 'Ir para revisão',
  cancelLabel: 'Cancelar',
  outlineCancelButton: true,
  type: 'warning',
  title: 'Tem certeza que deseja cancelar sua compra?',
  subtitle: 'Se prosseguir com essa etapa, todo o processo de compra será cancelado.',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/pet-warning.svg`,
  noPadding: true,
  showClose: false,
  dataTest: 'package-cancel',
  onConfirm: onConfirm,
  onCancel: onCancel,
  onClose: onClose,
  iconSize: '100%'
})

const INFO_COUPON = ({ body }) => ({
  cancelLabel: 'Entendi',
  type: 'info',
  body,
  title: 'Regra de uso: Cupons',
  icon: `${assetPrefix}/static/icons/tag-coupon.svg`,
  showBackButton: false,
  showClose: true
})

const COUPON_LIMIT_REACHED_ERROR = (onConfirm, errorTitle) => ({
  type: 'warning',
  title: errorTitle,
  body: 'Um dos cupons aplicados foi esgotado. Volte e remova para seguir com seus agendamentos',
  fullButton: true,
  iconSize: '100%',
  icon: `${assetPrefix}/static/icons/pet-digging.svg`,
  onConfirm: onConfirm,
  confirmLabel: 'Voltar'
})

export const SCHEDULING_TERMS = ({ visible, onClose }) => ({
  visible,
  onClose,
  children: <SchedulingTerms onClose={onClose} />
})

const SCHEDULING_SUCCESS = (onConfirm) => ({
  type: 'warning',
  title: 'Agendamento realizado com sucesso',
  body: (
    <>
      Em breve você receberá o resumo do seu agendamento em seu email.<br />
      Você também pode conferir detalhes em ”Minha agenda”
    </>
  ),
  confirmLabel: 'Fechar',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/scheduling-success.svg`,
  onConfirm: onConfirm,
  iconSize: '100%'
})

const REQUEST_CANCEL_PACKAGE = ({ onConfirm, onCancel }) => ({
  type: 'warning',
  title: 'Confirma a solicitação de cancelamento do pacote?',
  body: (
    <>
      Em até 48h você receberá um email com mais informações.<br />
      Importante: O prazo de cancelamento e devolução do valor podem levar até 60 dias. Este prazo não inclui os prazos da operadora do seu cartão de crédito.
    </>
  ),
  fullButton: true,
  icon: `${assetPrefix}/static/icons/pet-sad.svg`,
  onConfirm: onConfirm,
  onCancel: onCancel,
  confirmLabel: 'Solicitar cancelamento',
  cancelLabel: 'Voltar',
  outlineCancelButton: true,
  isCancel: true
})

const REQUEST_CANCEL_PACKAGE_SUCCESS = (onConfirm) => ({
  type: 'warning',
  title: 'Solicitação de cancelamento enviada',
  body: 'Em até 48h você receberá mais informações no seu email de cadastro. ',
  confirmLabel: 'Fechar',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/pet-info.svg`,
  onConfirm: onConfirm,
  onCancel: onConfirm
})

const REQUEST_CANCEL_PACKAGE_ERROR = ({ onCancel }) => ({
  type: 'warning',
  title: 'Solicitação não enviada',
  body: (
    <>
      Identificamos uma instabilidade no momento.
      <br />
      Por favor, tente novamente mais tarde.
    </>
  ),
  cancelLabel: 'Fechar',
  fullButton: true,
  icon: `${assetPrefix}/static/icons/pet-error.svg`,
  onCancel: onCancel
})

const REPEAT_PACKAGE_ADDITIONAL_SERVICES_UNAVAILABLE = (onConfirm, onCancel, partnerPhone) => ({
  visible: true,
  type: 'warning',
  title: 'Serviços adicionais indisponíveis para repetir',
  confirmLabel: 'Novo agendamento',
  onConfirm: onConfirm,
  cancelLabel: 'Ligue aqui',
  onCancel: onCancel,
  icon: `${assetPrefix}/static/icons/cat-warning.svg`,
  outlineCancelButton: true,
  fullButton: true,
  iconSize: '100%',
  body: (
    <ModalInfo>
      Os serviços adicionais deste agendamento não estão mais disponíveis na cota do seu pacote. Entre em contato com a loja para saber como seguir ou agende um novo atendimento. Ligue {`${partnerPhone}`}
    </ModalInfo>
  )
})

const REPEAT_PACKAGE_FINISHED_ERROR = (onConfirm, onCancel) => ({
  visible: true,
  type: 'warning',
  title: 'Você já utilizou todo o pacote de banho',
  confirmLabel: 'Ir para agendamento',
  onConfirm: onConfirm,
  cancelLabel: 'Agora não',
  onCancel: onCancel,
  icon: `${assetPrefix}/static/icons/dog-warning.svg`,
  outlineCancelButton: true,
  fullButton: true,
  iconSize: '100%',
  body: <ModalInfo>Siga para o agendamento para marcar um serviço avulso ou contratar um novo pacote de banho </ModalInfo>
})

const getNormalizedPhone = (phone) => phone?.replace(/\D/g, '')

const PACKAGE_PROFESSIONAL_UNAVAILABLE = ({
  mobile,
  partnerPhone,
  partnerName,
  onNewScheduling,
  onClose,
  confirmLabel,
  cancelLabel,
  body,
  onConfirm,
  onCancel
}) => {
  const normalizedPhone = getNormalizedPhone(partnerPhone)

  const handleContactPartner = () => {
    if (normalizedPhone) {
      window.location.href = `tel:${normalizedPhone}`
    }

    onClose()
  }

  const handleNewScheduling = () => {
    onClose()
    typeof onNewScheduling === 'function' && onNewScheduling()
  }

  const defaultBody = mobile
    ? 'O profissional deste serviço não está disponível no momento. Você pode agendar com outro profissional ou entrar em contato com a unidade para ajustar seu pacote.'
    : (
      <>
        O profissional deste serviço não está disponível no momento.
        <br />
        Você pode agendar com outro profissional ou entrar em contato com a unidade para ajustar seu pacote.
        {partnerPhone && (
          <>
            <br />
            Unidade {partnerName}: <b>{partnerPhone}</b>
          </>
        )}
      </>
    )

  return {
    icon: `${assetPrefix}/static/icons/warning-pet-generic.svg`,
    type: 'warning',
    title: 'Profissional indisponível',
    body: body || defaultBody,
    outlineCancelButton: true,
    fullButton: true,
    confirmLabel: confirmLabel || (mobile ? (<><PhoneAltIcon /> <span>Entrar em contato</span></>) : 'Novo agendamento'),
    cancelLabel: cancelLabel || (mobile ? 'Novo agendamento' : 'Voltar'),
    onConfirm: onConfirm || (mobile ? handleContactPartner : handleNewScheduling),
    onCancel: onCancel || (mobile ? handleNewScheduling : onClose),
    fullIcon: true
  }
}

export default {
  SCHEDULE_ERROR_GENERIC,
  PARTNER_NOT_FOUND,
  EDIT_PET_WARNING,
  NEW_PET_WARNING,
  LOCATION_NOT_ALLOWED,
  DUPLICATED_SCHEDULING_WARNING,
  PROFESSIONAL_PROFILE,
  RESCHEDULING,
  RESCHEDULING_SUCCESS,
  LOGIN_MODAL,
  LOGGOUT_MODAL,
  SCHEDULE_RESERVED_LOGIN,
  CREATE_ACCOUNT,
  USE_TERMS,
  PAYMENT_PACKAGE_TERMS,
  PACKAGE_ADHESION_TERMS,
  CONFIRM_NEW_ACCOUNT,
  FORGOT_PASSWORD_MODAL,
  RESET_PASSWORD_MODAL,
  SUCCESS_GENERIC,
  INCOMPLETE_PET_FORM,
  PET_SAVE_SUCCESS,
  EDIT_MULTIPLE_SCHEDULING,
  STEP_UPDATE,
  REPEAT_SCHEDULE_DATE_SELECTION,
  REPEAT_PACKAGE_ADDITIONAL_SERVICES_UNAVAILABLE,
  REPEAT_PACKAGE_FINISHED_ERROR,
  PAYMENT_PROBLEM,
  PAYMENT_SUCCESS,
  PAYMENT_PROCESSING,
  ACCOUNT_CREATE_SUCCESS,
  INFO_COUPON,
  COUPON_LIMIT_REACHED_ERROR,
  SCHEDULING_SUCCESS,
  PACKAGE_CANCEL,
  REQUEST_CANCEL_PACKAGE,
  REQUEST_CANCEL_PACKAGE_SUCCESS,
  REQUEST_CANCEL_PACKAGE_ERROR,
  SCHEDULING_TERMS,
  PACKAGE_PROFESSIONAL_UNAVAILABLE
}
